import { Box, Checkbox, Divider, Grid, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CertificateValidityPeriod } from 'src/enums'
import { IMainComponentType } from 'src/generated-types/main-component-type'
import { EnumToSelectTypes } from 'src/generated-types/select-type'
import { Flex } from 'src/styles/flexComponent'
import { Padding } from 'src/styles/paddingComponent'
import { useGetFile } from '../../../api/file'
import { IPaginatedResult } from '../../../api/paginated-result'
import BasicDatePicker from '../../../components/DateTimePicker'
import { FormFileCoreUpload, FormSelectField, FormTextField } from '../../../components/FormCore'
import { SuppliersFormField } from '../../../components/SuppliersFormField'
import { ICustomerAdminSupplier, IFile } from '../../../generated-types'
import { ICertificate } from '../../../generated-types/Certificate'
import { ICertificateDocumentType } from '../../../generated-types/certificate-document-type'

/**
 * Calculate the valid until date based on certificate validity period
 * @param certificateDate - The certificate issuance date
 * @param validityPeriod - The validity period type from CertificateValidityPeriod enum
 * @returns The calculated valid until date, or undefined for Custom period
 */
const calculateValidUntilDate = (
  certificateDate: Date | undefined,
  validityPeriod: CertificateValidityPeriod | undefined,
): Date | undefined => {
  if (!certificateDate || !validityPeriod || validityPeriod === CertificateValidityPeriod.Custom) {
    return undefined
  }

  const validUntilDate = new Date(certificateDate)

  switch (validityPeriod) {
    case CertificateValidityPeriod.oneYear:
      validUntilDate.setFullYear(validUntilDate.getFullYear() + 1)
      break
    case CertificateValidityPeriod.twoYear:
      validUntilDate.setFullYear(validUntilDate.getFullYear() + 2)
      break
    case CertificateValidityPeriod.threeYear:
      validUntilDate.setFullYear(validUntilDate.getFullYear() + 3)
      break
    case CertificateValidityPeriod.EighteenMonths:
      validUntilDate.setMonth(validUntilDate.getMonth() + 18)
      break
    case CertificateValidityPeriod.TwentyYears:
      validUntilDate.setFullYear(validUntilDate.getFullYear() + 20)
      break
    default:
      return undefined
  }

  return validUntilDate
}

interface Props {
  accordingSummary: string
  form: any
  suppliers?: IPaginatedResult<ICustomerAdminSupplier> | undefined
  initialValues?: any
  isProductCertificateAdd?: boolean
  initialCertificateFiles?: IFile | undefined
  setInitialCertificateFiles?: React.Dispatch<React.SetStateAction<IFile | null | undefined>>
  mainComponentTypeDetails?: {
    mainComponentType: IMainComponentType | undefined
    isCertificateDetailsRequired: boolean
  }
  setMainComponentTypeDetails?: React.Dispatch<
    React.SetStateAction<{
      mainComponentType: IMainComponentType | undefined
      isCertificateDetailsRequired: boolean
    }>
  >
}

export const MainComponentCertificationAdd: React.FC<Props> = ({
  accordingSummary,
  form,
  suppliers,
  initialValues,
  isProductCertificateAdd,
  initialCertificateFiles,
  setInitialCertificateFiles,
  mainComponentTypeDetails,
  setMainComponentTypeDetails,
}) => {
  const { t: translate } = useTranslation(['main-component', 'common', 'certificate'])
  const [certificateFileId, setCertificateFileId] = useState<number | undefined>(form.getValues('certificateFileId'))
  const { data: certificateFile, refetch } = useGetFile(certificateFileId as number)
  const validPeriod = form.watch('validPeriod')
  const validUntil = form.watch('validUntil')
  const certificateDate = form.watch('certificateDate')

  useEffect(() => {
    setInitialCertificateFiles && setInitialCertificateFiles(certificateFile)
  }, [certificateFile, setInitialCertificateFiles])

  useEffect(() => {
    const productCertificate = initialValues && (initialValues['certificate'] as ICertificate)
    if (productCertificate) {
      setCertificateFileId(productCertificate?.fileId)

      form.reset({
        ...initialValues,
        certificateId: productCertificate?.id,
        certificateNumber: productCertificate?.certificateNumber,
        certificateDate: productCertificate?.certificateDate,
        validUntil: productCertificate?.validUntil,
        validPeriod: productCertificate?.validPeriod,
        certificateFileId: productCertificate?.fileId,
        certificateDocumentType: productCertificate?.certificateDocumentType,
        certificateSupplierId: suppliers?.items.find(s => s.id == productCertificate?.supplierId) ?? null,
      })
    } else {
      setCertificateFileId(undefined)
    }
  }, [initialValues, suppliers, form])

  useEffect(() => {
    refetch()
  }, [certificateFileId, refetch])

  // Auto-calculate validUntil when validPeriod or certificateDate changes
  useEffect(() => {
    if (validPeriod?.id && validPeriod.id !== CertificateValidityPeriod.Custom && certificateDate) {
      const calculatedDate = calculateValidUntilDate(certificateDate, validPeriod.id)
      if (calculatedDate) {
        form.setValue('validUntil', calculatedDate, { shouldValidate: true })
      }
    } else if (validPeriod?.id !== CertificateValidityPeriod.Custom) {
      form.setValue('validUntil', undefined, { shouldValidate: true })
      form.clearErrors('validUntil')
    }
  }, [validPeriod?.id, certificateDate])

  useEffect(() => {
    if (validUntil) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      const validUntilDate = new Date(validUntil)
      validUntilDate.setHours(0, 0, 0, 0)

      if (validUntilDate < today) {
        form.setError('certificateDate', {
          type: 'manual',
          message: translate('certificateExpired', {
            ns: 'main-component',
            date: `${validUntilDate.getFullYear()}-${validUntilDate.getMonth() + 1}-${validUntilDate.getDate()}`,
          }),
        })
      } else {
        form.clearErrors('certificateDate')
      }
    }
  }, [validUntil, form, translate])

  const certificateDocumentTypes = Object.keys(ICertificateDocumentType)
    .filter(v => isNaN(Number(v)))
    .map(t => ICertificateDocumentType[t])
  const validPeriods = EnumToSelectTypes(CertificateValidityPeriod, 'certificate')

  return (
    <>
      <Padding.pt16>
        <Flex.Row px={1} justifyContent={'space-between'}>
          <Typography variant="subtitle2">{translate(accordingSummary, { ns: 'main-component' })}</Typography>
          {mainComponentTypeDetails?.mainComponentType && (
            <Checkbox
              disabled={setMainComponentTypeDetails == undefined}
              checked={mainComponentTypeDetails?.isCertificateDetailsRequired}
              onChange={(e, v) => {
                setMainComponentTypeDetails &&
                  setMainComponentTypeDetails(prev => ({
                    ...prev,
                    isCertificateDetailsRequired: v,
                  }))
              }}
            />
          )}
        </Flex.Row>
        {mainComponentTypeDetails?.isCertificateDetailsRequired && (
          <Padding.pt4>
            <Box px={1}>
              <Divider />
            </Box>
            <Grid container pt={1} className="!flex !flex-wrap justify-between">
              <Grid item xs={6}>
                <FormTextField
                  name="certificateId"
                  sx={{ display: 'none' }}
                  disabled={
                    !(mainComponentTypeDetails == null) && !mainComponentTypeDetails.isCertificateDetailsRequired
                  }
                />
                <FormTextField
                  fullWidth
                  name="certificateNumber"
                  label={translate('number', { ns: 'certificate' })}
                  disabled={
                    !(mainComponentTypeDetails == null) && !mainComponentTypeDetails.isCertificateDetailsRequired
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <FormSelectField
                  name="certificateDocumentType"
                  data={certificateDocumentTypes}
                  label={translate('documentType', { ns: 'certificate' })}
                  getOptionLabel={option => translate(ICertificateDocumentType[option], { ns: 'certificate' })}
                  renderOption={(props: any, option) => (
                    <Box {...props} key={option.id}>
                      {translate(ICertificateDocumentType[option], { ns: 'certificate' })}
                    </Box>
                  )}
                  isOptionEqualToValue={(o, v) => o === v}
                  defaultValue={ICertificateDocumentType.ProductCertificate}
                  disabled={
                    (!(mainComponentTypeDetails == null) && !mainComponentTypeDetails.isCertificateDetailsRequired) ||
                    isProductCertificateAdd
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <BasicDatePicker
                  label={translate('certificateDate', { ns: 'certificate' })}
                  name="certificateDate"
                  disabled={
                    !(mainComponentTypeDetails == null) && !mainComponentTypeDetails.isCertificateDetailsRequired
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <SuppliersFormField
                  suppliers={suppliers?.items as ICustomerAdminSupplier[]}
                  name="certificateSupplierId"
                  label={translate('certifiedBy', { ns: 'certificate' })}
                  disabled={
                    !(mainComponentTypeDetails == null) && !mainComponentTypeDetails.isCertificateDetailsRequired
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <FormSelectField
                  name="validPeriod"
                  data={validPeriods}
                  label={translate('selectDuration', { ns: 'certificate' })}
                  getOptionLabel={option => option.value}
                  renderOption={(props: any, option) => (
                    <Box {...props} key={option.id}>
                      {option.value}
                    </Box>
                  )}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                />
              </Grid>
              <Grid item xs={6}>
                <BasicDatePicker
                  label={translate('validUntil', { ns: 'certificate' })}
                  name="validUntil"
                  disabled={validPeriod?.id !== CertificateValidityPeriod.Custom}
                />
              </Grid>
              <Grid item xs={12}>
                <FormFileCoreUpload
                  height="175px"
                  label={translate('fileName', { ns: 'common' })}
                  name="certificateFileId"
                  mapFileKey="id"
                  initialFiles={initialCertificateFiles}
                  disabled={
                    !(mainComponentTypeDetails == null) && !mainComponentTypeDetails.isCertificateDetailsRequired
                  }
                  getUploadedFiles={e => setInitialCertificateFiles && setInitialCertificateFiles(e)}
                  acceptedFileTypes={[
                    'application/pdf',
                    'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'image/jpg',
                    'image/jpeg',
                    'image/png',
                  ]}
                  showFileTypes={false}
                />
              </Grid>
            </Grid>
          </Padding.pt4>
        )}
      </Padding.pt16>
    </>
  )
}
